import { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import io from "socket.io-client";
import { apiHost, apiV2Host, locationsApiHost } from "common/config";
import {
  SHIPMENTS_SOCKET_CREATE,
  SHIPMENTS_SOCKET_UPDATE,
  SHIPMENTS_SOCKET_DELETE
} from "state/shipment";
import { MESSAGE_UPDATE } from "state/dialog";
import {
  MEMBER_CONNECTIVITY_CHANGE,
  DRIVER_LOCATION_UPDATE
} from "state/driver";
import { loadDrivers } from "state/driver/driverAction";
import { forceGetShipments } from "state/entities/shipments/action";
import {
  isWencoDemoModeEnabled,
  WENCO_DEMO_MODE_CHANGED
} from "common/wencoDemoMode";
import { startOfDay } from "date-fns";
import moment from "moment";

// emit events
const EVENT_EMIT_AUTHENTICATE = "authenticate";

// listener events
const EVENT_SHIPMENTS_CREATE = "shipments_create";
const EVENT_SHIPMENTS_UPDATE = "shipments_update";
const EVENT_SHIPMENTS_DELETE = "shipments_delete";
const EVENT_CHATMESSAGE_CREATE = "chatmessage_create";
const EVENT_CHATMESSAGE_UPDATE = "chatmessage_update";
const EVENT_LOCATION_CREATE_V1 = "location_create";
const EVENT_LOCATION_CREATE = "locations_create";
const EVENT_MEMBER_CONNECTIVITY_CHANGE = "member_connectivity_change";
const EVENT_LOCATION_REGISTER = "join_logisticcompany";

/**
 * Same effective company as API requests (X-Logisticcompany-ID), not only the user's home company.
 */
function resolveSocketLogisticCompanyId(logisticCompanyId, user) {
  if (logisticCompanyId && logisticCompanyId !== 0) {
    return logisticCompanyId;
  }
  return user?.logisticcompany_id;
}

/**
 * Handles socket connection and calls redux events to update the state.
 */
const SocketHandler = ({ token, dispatch, user, logisticCompanyId }) => {
  const [connected, setConnected] = useState(false);
  const [connectedV2, setConnectedV2] = useState(false);
  const [connectedLocation, setConnectedLocation] = useState(false);
  const [wencoDemoEnabled, setWencoDemoEnabled] = useState(
    isWencoDemoModeEnabled()
  );

  // The “ref” object is a generic container whose current property
  // is mutable and can hold any value, similar to an instance property on a class.
  // See https://reactjs.org/docs/hooks-faq.html#is-there-something-like-instance-variables
  const socket = useRef(null);
  const socketV2 = useRef(null);
  const socketLocation = useRef(null);

  useEffect(() => {
    const onWencoDemoModeChanged = event => {
      setWencoDemoEnabled(Boolean(event?.detail?.enabled));
    };
    window.addEventListener(WENCO_DEMO_MODE_CHANGED, onWencoDemoModeChanged);
    return () =>
      window.removeEventListener(
        WENCO_DEMO_MODE_CHANGED,
        onWencoDemoModeChanged
      );
  }, []);

  useEffect(() => {
    if (wencoDemoEnabled) {
      setConnected(false);
      setConnectedV2(false);
      setConnectedLocation(false);
      return undefined;
    }

    socket.current = io(apiHost);
    socketV2.current = io(apiV2Host, {
      path: "/v2/socket.io"
    });
    // socketLocation.current = io(locationsApiHost);
    socketLocation.current = io(locationsApiHost, {
      transports: ["websocket"],  // kein Polling → io-Cookie oft irrelevant
    });

    // socketV2.current = io("https://api.test.trackcode.de", {
    //   path: "/v2"
    // });
    // initialize event listeners v1
    socket.current.on("connect", () => {
      //console.log("Socket connected");
      setConnected(true);
    });
    socket.current.on("disconnect", () => {
      //console.log("Socket disconnected");
      setConnected(false);
    });
    socket.current.on(EVENT_SHIPMENTS_CREATE, ({ content }) => {
      //console.log(EVENT_SHIPMENTS_CREATE, content);
      dispatch({
        type: SHIPMENTS_SOCKET_CREATE,
        payload: content
      });
    });
    socket.current.on(EVENT_SHIPMENTS_UPDATE, ({ content }) => {
      //console.log(EVENT_SHIPMENTS_UPDATE, content);
      dispatch({
        type: SHIPMENTS_SOCKET_UPDATE,
        payload: content
      });
    });
    socket.current.on(EVENT_SHIPMENTS_DELETE, ({ content }) => {
      //console.log(EVENT_SHIPMENTS_DELETE, content);
      dispatch({
        type: SHIPMENTS_SOCKET_DELETE,
        payload: content
      });
    });
    socket.current.on(EVENT_CHATMESSAGE_CREATE, message => {
      //console.log(EVENT_CHATMESSAGE_CREATE, message);
      dispatch({
        type: MESSAGE_UPDATE,
        payload: message
      });
    });
    socket.current.on(EVENT_CHATMESSAGE_UPDATE, message => {
      //console.log(EVENT_CHATMESSAGE_UPDATE, message);
      dispatch({
        type: MESSAGE_UPDATE,
        payload: message
      });
    });
    socket.current.on(EVENT_LOCATION_CREATE_V1, ({ content }) => {
      dispatch({
        type: DRIVER_LOCATION_UPDATE,
        payload: content
      });
    });
    socket.current.on(EVENT_MEMBER_CONNECTIVITY_CHANGE, ({ content }) => {
      //console.log(EVENT_MEMBER_CONNECTIVITY_CHANGE, content);
      dispatch({
        type: MEMBER_CONNECTIVITY_CHANGE,
        payload: content
      });
    });

    // ######### V2 SOCKET
    socketV2.current.on("connect", () => {
      const companyId = resolveSocketLogisticCompanyId(
        logisticCompanyId,
        user
      );
      if (companyId) {
        socketV2.current.emit("join_logisticcompany", companyId);
      }
      setConnectedV2(true);
    });
    socketV2.current.on("disconnect", () => {
      //console.log("Socket v2 disconnected");
      setConnectedV2(false);
    });

    socketV2.current.on(EVENT_MEMBER_CONNECTIVITY_CHANGE, content => {
      //console.log(EVENT_MEMBER_CONNECTIVITY_CHANGE, content);
      dispatch({
        type: MEMBER_CONNECTIVITY_CHANGE,
        payload: content
      });

      dispatch(loadDrivers(token, ""));
    });

    socketV2.current.on(EVENT_SHIPMENTS_CREATE, content => {
      //console.log(EVENT_SHIPMENTS_CREATE, content);

      const start = startOfDay(new Date());
      const end = moment(new Date()).endOf("day");
      dispatch(loadDrivers(token, ""));
      dispatch(forceGetShipments(start, end));
    });

    // ######### LOCATION SOCKET
    socketLocation.current.on("connect", () => {
      const companyId = resolveSocketLogisticCompanyId(
        logisticCompanyId,
        user
      );
      if (companyId) {
        socketLocation.current.emit(EVENT_LOCATION_REGISTER, companyId);
      }

      setConnectedLocation(true);
    });
    socketLocation.current.on("disconnect", () => {
      //console.log("Socket Location disconnected");
      setConnectedLocation(false);
    });

    socketLocation.current.on(EVENT_LOCATION_CREATE, content => {
      //console.log(EVENT_LOCATION_CREATE, content);
      dispatch({
        type: DRIVER_LOCATION_UPDATE,
        payload: content
      });
    });

    return () => {
      // close socket on end of lifetime
      if (socket.current) socket.current.close();
      if (socketV2.current) socketV2.current.close();
      if (socketLocation.current) socketLocation.current.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.logisticcompany_id, wencoDemoEnabled]);

  useEffect(() => {
    const companyId = resolveSocketLogisticCompanyId(logisticCompanyId, user);
    if (!companyId) {
      return;
    }

    if (connected) {
      socket.current.emit(EVENT_EMIT_AUTHENTICATE, {
        token,
        logisticCompanyId: companyId
      });
    }

    if (connectedV2 && socketV2.current) {
      socketV2.current.emit("join_logisticcompany", companyId);
    }

    if (connectedLocation && socketLocation.current) {
      socketLocation.current.emit(EVENT_LOCATION_REGISTER, companyId);
      socketLocation.current.emit(EVENT_EMIT_AUTHENTICATE, {
        token,
        logisticCompanyId: companyId
      });
    }
  }, [
    connected,
    connectedV2,
    connectedLocation,
    token,
    logisticCompanyId,
    user?.logisticcompany_id
  ]);

  return null;
};

const mapStateToProps = ({
  auth: { token, logisticCompanyId, user }
}) => ({
  token,
  logisticCompanyId
});

// export it for testing
export const SocketHandlerComponent = SocketHandler;

export default connect(mapStateToProps)(SocketHandler);
